import { useEffect, useMemo, useState } from 'react';
import useToast from 'components/hooks/useToast';
import {
  createIntegrationToken,
  deleteIntegrationToken,
  getIntegrationTokens,
  setIntegrationTokenPrimary
} from 'common/services/integration-tokens.service';
import useTableSortBy from 'common/fetchHooks/useTableSortBy';
import { useDebounceCallback } from '@react-hook/debounce';
import { SENSOR_STATES } from '@linearb/linjs-common';

const useMultiTokens = ({ integration }) => {
  const isEnabled = !!integration?.is_multi_token_enabled;

  const [response, setResponse] = useState({});
  const [isLoading, setIsLoading] = useState(isEnabled);
  const sortBy = useTableSortBy('created_at');

  const toast = useToast();
  const integrationId = integration?.id;

  const tokensList = useMemo(() => response?.items || [], [response]);
  const total = useMemo(() => response?.total || 0, [response]);

  const primaryToken = useMemo(
    () => tokensList.find(token => token.is_primary),
    [tokensList]
  );
  const isPrimaryTokenFailed = useMemo(
    () => !!primaryToken && primaryToken.state !== SENSOR_STATES.ok,
    [primaryToken]
  );

  const toastError = (message = 'Oops. something went wrong') =>
    toast({ variant: 'error', message });

  const fetchTokens = useDebounceCallback(async () => {
    try {
      setResponse({});
      setIsLoading(true);
      const res = await getIntegrationTokens(integration.id, {
        order_by: sortBy.key,
        order_dir: sortBy.dir
      });
      setResponse(res);
    } catch (e) {
    } finally {
      setIsLoading(false);
    }
  }, 100);

  const createToken = async ({ token, username }) => {
    try {
      setIsLoading(true);
      await createIntegrationToken({ token, integrationId, username });
      toast({ message: 'The token created successfully' });
    } catch (e) {
      const errorType = e?.response?.data?.error;
      switch (errorType) {
        case 'Conflict':
          return toastError(e.response.data.description);
        default:
          return toastError();
      }
    } finally {
      fetchTokens();
    }
  };

  const deleteToken = async tokenId => {
    try {
      const confirmed = window.confirm(
        'Are you sure you want to delete this token?'
      );
      if (!confirmed) return;

      setIsLoading(true);
      await deleteIntegrationToken({ integrationId, tokenId });
      toast({ message: 'The token deleted succesfully' });
    } catch (e) {
      toastError();
    } finally {
      fetchTokens();
    }
  };

  const setTokenPrimary = async tokenId => {
    try {
      setIsLoading(true);
      await setIntegrationTokenPrimary({ integrationId, tokenId });
    } catch (e) {
      toastError();
    } finally {
      fetchTokens();
    }
  };

  useEffect(() => {
    if (!isEnabled) return;
    fetchTokens();
  }, [integrationId, sortBy.key, sortBy.dir]);

  return {
    isEnabled,
    isLoading,
    sortBy,
    tokensList,
    total,
    createToken,
    deleteToken,
    setTokenPrimary,
    primaryToken,
    isPrimaryTokenFailed
  };
};

export default useMultiTokens;
